/**
 * Surface detail for sprites at double resolution: fur, scales, chitin, feathers, leaves and bark,
 * stone. Scale2x only rounds a sprite's outline; this paints in what a 1× sprite has no room for,
 * in half-pixel strokes, keeping its colours, outlines and eyes. Light comes from the upper right,
 * as on the figures: lit toward a warm cream, shaded toward a cool violet.
 */

export type Detail = 'fur' | 'scales' | 'chitin' | 'feathers' | 'plant' | 'stone';

/** A cheap repeatable hash of a cell, 0–1. */
const hash = (x: number, y: number): number => {
  const n = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
  return n - Math.floor(n);
};

/** A pixel (little-endian RGBA) lit (k > 0) or shaded (k < 0) by `k`. */
function tone(px: number, k: number): number {
  const r = px & 255;
  const g = (px >>> 8) & 255;
  const b = (px >>> 16) & 255;
  const to = (v: number, warm: number, cool: number) =>
    Math.max(0, Math.min(255, Math.round(k > 0 ? v + (warm - v) * k : v + (cool * 0.45 - v) * -k)));
  return (px & 0xff000000) | (to(b, 205, 96) << 16) | (to(g, 240, 40) << 8) | to(r, 255, 44);
}

const lum = (px: number) => (px & 255) * 0.3 + ((px >>> 8) & 255) * 0.59 + ((px >>> 16) & 255) * 0.11;

/** Paints `kind` of detail into the pixels `o` (W×H, little-endian RGBA) of a doubled sprite. */
export function addDetail(o: Uint32Array, W: number, H: number, kind: Detail): void {
  // Each column's top and bottom, for the light falling from above and the shade underneath.
  const top = new Int32Array(W).fill(H);
  const bot = new Int32Array(W).fill(-1);
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++)
      if (o[y * W + x]! >>> 24) {
        if (y < top[x]!) top[x] = y;
        bot[x] = y;
      }
  const src = o.slice();
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      const i = y * W + x;
      const px = src[i]!;
      if (!(px >>> 24)) continue;
      const l = lum(px);
      // Outlines, eyes and the darkest marks stay exactly as drawn; so does snow.
      if (l < 34 || l > 236) continue;
      const span = Math.max(1, bot[x]! - top[x]!);
      const v = (y - top[x]!) / span;
      let k = 0;
      switch (kind) {
        case 'fur': {
          // Round body: the back catches the light, the belly falls into shade.
          k = 0.12 * (1 - 2 * v);
          // Short strands slanting back along the body, two half-pixels wide and three long.
          const s = hash(Math.floor((x + y * 0.5) / 2), Math.floor(y / 3));
          if (s < 0.2) k -= 0.16;
          else if (s > 0.86 && v < 0.55) k += 0.14;
          break;
        }
        case 'scales': {
          k = 0.1 * (1 - 2 * v);
          // Overlapping scales in offset rows: a dark rim under each, a glint along its top.
          const row = Math.floor(y / 3);
          const u = (x + (row % 2) * 2) % 4;
          const w = y % 3;
          if (w === 2) k -= 0.22;
          else if (w === 0 && (u === 1 || u === 2)) k += 0.16;
          break;
        }
        case 'chitin': {
          // A hard shell: a strong shine along the top, plates divided by dark seams, glints.
          k = v < 0.18 ? 0.3 : 0.1 - 0.3 * v;
          if (x % 6 === 0) k -= 0.2;
          if (hash(x, y) < 0.03 && v < 0.5) k += 0.45;
          break;
        }
        case 'feathers': {
          k = 0.1 * (1 - 2 * v);
          // Scalloped feathers in offset rows, each edged darker where it overlaps the next.
          const row = Math.floor(y / 4);
          const u = (x + (row % 2) * 3) % 6;
          const w = y % 4;
          if (w === 3 && u > 0 && u < 5) k -= 0.18;
          else if (w === 0 && u > 1 && u < 4) k += 0.12;
          break;
        }
        case 'plant': {
          const r = px & 255;
          const g = (px >>> 8) & 255;
          const b = (px >>> 16) & 255;
          if (g >= r && g > b) {
            // Leaves: little clusters each lit on its upper right and shaded on its lower left.
            k = 0.08 * (1 - 2 * v);
            const cx = Math.floor(x / 3);
            const cy = Math.floor(y / 3);
            if (hash(cx, cy) < 0.45) {
              if (x % 3 === 2 && y % 3 === 0) k += 0.2;
              else if (x % 3 === 0 && y % 3 === 2) k -= 0.18;
            }
          } else if (r > b) {
            // Bark: grain running up the trunk, with the odd lighter ridge.
            const s = hash(x, Math.floor(y / 4));
            if (s < 0.25) k -= 0.16;
            else if (s > 0.9) k += 0.12;
          }
          break;
        }
        case 'stone': {
          k = 0.14 * (1 - 2 * v);
          // Grain, and here and there a hairline crack.
          const s = hash(x, y);
          if (s < 0.1) k -= 0.12;
          else if (s > 0.93) k += 0.12;
          if (hash(Math.floor(x / 5), Math.floor(y / 5)) < 0.14 && (x + y) % 5 === 0) k -= 0.25;
          break;
        }
      }
      if (k) o[i] = tone(px, k);
    }
}
